<template>
  <div
    class="search-passengers"
    v-click-outside="close">
    <button
      class="search-passengers__field"
      type="button"
      :aria-expanded="isOpen"
      @click="toggle">
      <svg class="search-passengers__icon">
        <use :xlink:href="`#${person}`" />
      </svg>
      <span class="search-passengers__text">{{ passengerText }}</span>
      <svg
        class="search-passengers__chevron"
        :class="{ 'search-passengers__chevron--open': isOpen }">
        <use :xlink:href="`#${chevron}`" />
      </svg>
    </button>
    <!-- Desktop: inline dropdown panel -->
    <div
      v-if="isOpen && !isBelowLg"
      class="search-passengers__dropdown">
      <div class="search-passengers__panel">
        <PassengerPicker :initial-passengers="travelSearchStore.passengers" />
      </div>
    </div>

    <!-- Below lg: fullscreen sheet (no drag handle; X cancels, Lagre saves).
         Kept inside this single root so the parent's grid-placement class is
         inherited; BottomDrawer teleports to <body>, so nesting it here has no
         layout effect. -->
    <BottomDrawer
      v-if="isBelowLg"
      :is-open="isOpen"
      :full-screen="true"
      :hide-handle="true"
      @close="cancel">
      <template #header>
        <h2 class="search-passengers__drawer-title">{{ $t('common.passenger.title') }}</h2>
      </template>
      <PassengerPicker :initial-passengers="travelSearchStore.passengers" />
      <template #footer>
        <div class="search-passengers__nav">
          <button
            class="search-passengers__save"
            type="button"
            @click="save">
            {{ $t('common.actions.save') }}
          </button>
        </div>
      </template>
    </BottomDrawer>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, type Directive } from 'vue'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import person from '@/assets/icons/person.svg'
  import chevron from '@/assets/icons/chevron.svg'
  import PassengerPicker from './PassengerPicker.vue'
  import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'
  import { useMediaQuery } from '@/composables/useMediaQuery'
  import { t } from '@/i18n'
  import type { Passenger } from '@/types/passengerTypes'

  defineOptions({
    name: 'SearchPassengers',
  })

  const travelSearchStore = useTravelSearchStore()

  // Collapsed summary, e.g. "1 Voksen" or "3 passasjerer". The custom $t has no
  // interpolation, so the string is composed here.
  const passengerText = computed(() => {
    const passengers = travelSearchStore.passengers
    const counts = passengers.reduce(
      (acc, p) => {
        if (!p.type) return acc
        acc[p.type] = (acc[p.type] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )
    const codes = Object.keys(counts)
    if (codes.length === 0) return ''
    // Single type → "1 Voksen"; multiple types → "3 passasjerer".
    if (codes.length === 1) {
      return `${counts[codes[0]]} ${t(`descriptions.passengerNames.${codes[0]}`)}`
    }
    return `${passengers.length} ${t('common.passenger.passengers')}`
  })

  // Mirror SearchPage's layout switch: below $breakpoint-lg (1024px) the picker uses
  // the fullscreen BottomDrawer; at/above it uses the inline desktop dropdown.
  const isBelowLg = useMediaQuery('(max-width: 1023.98px)')

  const isOpen = ref(false)
  // Snapshot of the selection when the mobile sheet opens, so X / overlay can cancel.
  const snapshot = ref<Passenger[]>([])

  const vClickOutside: Directive<HTMLElement, () => void> = {
    beforeMount(el, binding) {
      ;(el as any).clickOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value()
        }
      }
      // Capture phase so the handler fires even if something along the bubble path
      // calls stopPropagation — a tap anywhere outside the picker closes it.
      document.addEventListener('click', (el as any).clickOutsideEvent, true)
    },
    unmounted(el) {
      document.removeEventListener('click', (el as any).clickOutsideEvent, true)
    },
  }

  // Bound to v-click-outside (desktop dropdown only). Below lg the sheet is teleported
  // to <body>, so dismissal there is handled by the drawer's X / overlay (→ cancel).
  const close = () => {
    if (isBelowLg.value) return
    isOpen.value = false
  }

  const toggle = () => {
    isOpen.value = !isOpen.value
    if (isOpen.value) {
      // Snapshot before any edits so the mobile sheet can cancel back to this state.
      snapshot.value = JSON.parse(JSON.stringify(travelSearchStore.passengers))
    }
  }

  // Mobile sheet: keep the live selection and close.
  const save = () => {
    isOpen.value = false
  }

  // Mobile sheet: restore the pre-open selection and close (X / overlay tap).
  const cancel = () => {
    travelSearchStore.setPassengers(snapshot.value)
    isOpen.value = false
  }
</script>

<style lang="scss" scoped>
  .search-passengers {
    position: relative;

    &__field {
      display: flex;
      align-items: center;
      gap: $search-field-icon-gap;
      width: 100%;
      min-height: $search-field-height;
      box-sizing: border-box;
      padding: 0 $search-field-padding-x;
      background-color: $clr-white;
      border: 1px solid $clr-stroke;
      border-radius: $border-radius;
      cursor: pointer;
      text-align: left;
    }

    &__icon {
      @include search-field-icon;
      flex-shrink: 0;
    }

    &__text {
      @include body-md(400);
      flex: 1 0 0;
      min-width: 0;
      color: $clr-light-primary-900;
    }

    &__chevron {
      width: rem(18);
      height: rem(18);
      flex-shrink: 0;
      color: $clr-primary-200;
      transition: transform 0.2s ease;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + #{rem(4)});
      left: 0;
      right: 0;
      z-index: 1000;
      // Transparent bottom padding lets the page show through beneath the panel AND
      // extends this element's border box, so the body can scroll past the panel on
      // short screens (a margin would not be counted in scrollable overflow).
      padding-bottom: rem(32);

      // On desktop the field shrinks to its narrow grid column, so widen the panel:
      // pin it to the field's right edge (right: 0 from above) and extend left.
      @media (min-width: $breakpoint-lg) {
        left: auto;
        width: rem(340);
      }
    }

    &__panel {
      background-color: $clr-white;
      border-radius: rem(6);
      box-shadow: $box-shadow-card-large;
      overflow: hidden;
    }

    // Mobile fullscreen sheet
    &__drawer-title {
      @include abcginto-heading;
      color: $clr-primary;
      margin: 0;
      padding: rem(16) 0;
    }

    // Footer bar around the action.
    &__nav {
      width: 100%;
      box-sizing: border-box;
      padding: rem(12) rem(16) rem(16);
      border-top: 1px solid $clr-gray-100;
      background-color: $clr-background;
    }

    &__save {
      width: 100%;
      box-sizing: border-box;
      min-height: rem(48);
      background-color: $clr-primary;
      color: $clr-white;
      border: none;
      border-radius: $border-radius;
      cursor: pointer;
      // Buttons don't inherit the page font (UA default = Arial); pull in Ginto.
      font-family: inherit;
      font-size: rem(18);
      font-weight: 500;
      line-height: rem(24);
    }
  }
</style>
